import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowLeft } from 'lucide-react';
import { useUserPrefs } from '../Components/context/UserPrefs';
import { api } from '../services/api';
import QrCode from '../Components/Common/QrCode';

export default function Seguridad() {
  const navigate = useNavigate();
  const { currentUser } = useUserPrefs();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [secretGrouped, setSecretGrouped] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    (async () => {
      try {
        const res = await api.auth.totp.status();
        setEnabled(!!res?.enabled);
        setAwaitingApproval(!!res?.awaiting_approval);
      } catch (e) {
        setError(e?.message || 'No fue posible consultar el estado del 2FA.');
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUser, navigate]);

  const startEnroll = async () => {
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const res = await api.auth.totp.start();
      if (!res?.ok) throw new Error(res?.error || 'No fue posible generar el código.');
      setSecretGrouped(res.secret_grouped || res.secret || '');
      setOtpauthUri(res.otpauth_uri || '');
      setEnrolling(true);
    } catch (e) {
      setError(e?.message || 'No fue posible iniciar la activación.');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmEnroll = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const res = await api.auth.totp.confirm(confirmCode);
      if (!res?.ok) throw new Error(res?.error || 'Código inválido.');
      setAwaitingApproval(true);
      setEnrolling(false);
      setConfirmCode('');
      setSecretGrouped('');
      setOtpauthUri('');
      setNotice('Código confirmado. Queda pendiente de que un Administrador apruebe la activación.');
    } catch (e) {
      setError(e?.message || 'Código inválido.');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelEnroll = () => {
    setEnrolling(false);
    setConfirmCode('');
    setSecretGrouped('');
    setOtpauthUri('');
    setError('');
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setSubmitting(true);
    try {
      const res = await api.auth.totp.disable(disablePassword);
      if (!res?.ok) throw new Error(res?.error || 'No fue posible desactivar.');
      setEnabled(false);
      setAwaitingApproval(false);
      setShowDisableForm(false);
      setDisablePassword('');
      setNotice('Verificación por app desactivada.');
    } catch (e) {
      setError(e?.message || 'Contraseña incorrecta.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft size={16} /> Volver
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Seguridad de la cuenta</h1>
            <p className="text-sm text-gray-500">{currentUser?.email || ''}</p>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Cargando...</p>
        ) : (
          <>
            {notice && (
              <div className="mb-4 px-3 py-2 rounded-lg border text-sm bg-green-50 border-green-200 text-green-700">
                {notice}
              </div>
            )}
            {error && (
              <div className="mb-4 px-3 py-2 rounded-lg border text-sm bg-red-50 border-red-200 text-red-700">
                {error}
              </div>
            )}

            <div className="rounded-xl border border-gray-200 p-4 mb-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Verificación en dos pasos por app autenticadora
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {enabled
                      ? 'Activa. Al iniciar sesión siempre te pedirá el código de tu app.'
                      : awaitingApproval
                        ? 'Confirmada, pero pendiente de que un Administrador la apruebe antes de poder volver a ingresar.'
                        : 'Inactiva. La primera vez que inicies sesión, el sistema te pedirá activarla.'}
                  </div>
                </div>
                <span
                  className={`shrink-0 text-xs font-semibold px-2 py-1 rounded-full ${
                    enabled ? 'bg-green-100 text-green-700' : awaitingApproval ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {enabled ? 'Activa' : awaitingApproval ? 'Pendiente de aprobación' : 'Inactiva'}
                </span>
              </div>

              {!enabled && !awaitingApproval && !enrolling && (
                <button
                  type="button"
                  onClick={startEnroll}
                  disabled={submitting}
                  className="mt-4 w-full py-2.5 rounded-lg text-white font-semibold disabled:opacity-60"
                  style={{ backgroundColor: '#2563EB' }}
                >
                  {submitting ? 'Generando...' : 'Activar verificación por app'}
                </button>
              )}

              {(enabled || awaitingApproval) && !showDisableForm && (
                <button
                  type="button"
                  onClick={() => setShowDisableForm(true)}
                  className="mt-4 w-full py-2.5 rounded-lg border border-red-200 text-red-600 font-semibold hover:bg-red-50"
                >
                  Desactivar
                </button>
              )}

              {showDisableForm && (
                <form onSubmit={submitDisable} className="mt-4 space-y-3">
                  <label className="block text-xs text-gray-600">
                    Confirma tu contraseña para desactivar
                  </label>
                  <input
                    type="password"
                    value={disablePassword}
                    onChange={(e) => setDisablePassword(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 py-2 rounded-lg text-white font-semibold disabled:opacity-60"
                      style={{ backgroundColor: '#DC2626' }}
                    >
                      {submitting ? 'Desactivando...' : 'Confirmar desactivación'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowDisableForm(false); setDisablePassword(''); }}
                      className="flex-1 py-2 rounded-lg border"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              )}
            </div>

            {enrolling && (
              <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4">
                <div className="text-sm font-medium text-gray-900 mb-2">
                  1. Abre tu app de códigos de verificación en el celular: Google Authenticator, Microsoft Authenticator, Authy, o cualquier otra similar (todas funcionan igual)
                </div>
                <div className="text-sm font-medium text-gray-900 mb-3">
                  2. Escanea este código QR (o usa "Ingresar clave manualmente" con la clave de abajo)
                </div>
                {otpauthUri && (
                  <div className="flex justify-center mb-3">
                    <QrCode value={otpauthUri} />
                  </div>
                )}
                <div className="text-xs text-gray-600 mb-1">Nombre de la cuenta: {currentUser?.email}</div>
                <div className="text-xs text-gray-600 mb-1">Clave manual (si no puedes escanear):</div>
                <div className="font-mono text-base tracking-widest bg-white border rounded-lg px-3 py-2 mb-3 select-all break-all">
                  {secretGrouped}
                </div>
                <div className="text-sm font-medium text-gray-900 mb-2">
                  3. Ingresa aquí el código de 6 dígitos que aparece en la app
                </div>
                <form onSubmit={confirmEnroll} className="space-y-3">
                  <input
                    type="text"
                    value={confirmCode}
                    onChange={(e) => setConfirmCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                    maxLength={6}
                    placeholder="000000"
                    className="w-full text-center text-2xl tracking-widest border rounded-lg px-3 py-2"
                    autoFocus
                    required
                  />
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={submitting || confirmCode.length !== 6}
                      className="flex-1 py-2 rounded-lg text-white font-semibold disabled:opacity-60"
                      style={{ backgroundColor: '#2563EB' }}
                    >
                      {submitting ? 'Verificando...' : 'Confirmar código'}
                    </button>
                    <button type="button" onClick={cancelEnroll} className="flex-1 py-2 rounded-lg border">
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
